import React, { useState } from "react";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import DateRangePresets from "../../../features/filter/TabFilterModal/DateRangePresets";

const GRANULARITY_OPTIONS = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const TransitionTimeLineChartSettings = ({
  title,
  objectTypes,
  selectedObjectType,
  availableResources,
  configuredResources,
  toleranceThreshold,
  enabledGranularities,
  onTitleChange,
  onObjectTypeChange,
  onConfiguredResourcesChange,
  onToleranceThresholdChange,
  onEnabledGranularitiesChange,
  dateRangeStart,
  dateRangeEnd,
  onDateRangePresetChange,
  loading = false,
}) => {
  const [thresholdInput, setThresholdInput] = useState(
    String(toleranceThreshold ?? 0),
  );
  const toggleGranularity = (id) => {
    if (enabledGranularities.includes(id)) {
      const next = enabledGranularities.filter((g) => g !== id);
      if (next.length > 0) {
        onEnabledGranularitiesChange(next);
      }
      return;
    }
    onEnabledGranularitiesChange([...enabledGranularities, id]);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Transition Time Line Chart"
          disabled={loading}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Object Type and Resources
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Object Type *
            </label>
            <select
              value={selectedObjectType}
              onChange={(e) => onObjectTypeChange(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={loading}
            >
              <option value="">Select Object Type</option>
              {(objectTypes || []).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <MultiSelectDropdown
              label="Configured Resources *"
              options={availableResources.map(String)}
              selectedValues={configuredResources}
              onSelectionChange={onConfiguredResourcesChange}
              placeholder={
                availableResources.length === 0
                  ? "Load data first..."
                  : "Select resources..."
              }
              disabled={loading || availableResources.length === 0}
              itemType="resources"
              explicitDisplayCount={5}
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Transition-time threshold (seconds)
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Transitions strictly below this value are plotted as median transition
          time per bucket. Values at or above the threshold are treated as
          downtime and excluded entirely.
        </p>
        <input
          type="number"
          value={thresholdInput}
          onChange={(e) => {
            const raw = e.target.value;
            setThresholdInput(raw);
            if (raw === "") return;
            const parsed = parseInt(raw, 10);
            if (!isNaN(parsed)) {
              onToleranceThresholdChange(Math.max(0, parsed));
            }
          }}
          onBlur={() => {
            const parsed = parseInt(thresholdInput, 10);
            const safe = isNaN(parsed) ? 0 : Math.max(0, parsed);
            setThresholdInput(String(safe));
            onToleranceThresholdChange(safe);
          }}
          min="0"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Enabled Granularities (View Mode)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {GRANULARITY_OPTIONS.map((option) => {
            const active = enabledGranularities.includes(option.id);
            return (
              <label
                key={option.id}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                  active
                    ? "bg-primary-50 border-primary-300"
                    : "bg-white border-gray-300"
                } ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleGranularity(option.id)}
                  className="rounded border-gray-300 focus:ring-primary-500"
                  style={{ accentColor: "#0066cc" }}
                  disabled={loading}
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {onDateRangePresetChange && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Date Range (Date Range Mode)
          </label>
          <DateRangePresets
            value={{ startDate: dateRangeStart || null, endDate: dateRangeEnd || null, enabled: true }}
            onChange={onDateRangePresetChange}
          />
        </div>
      )}
    </div>
  );
};

export default TransitionTimeLineChartSettings;
